/* eslint-disable camelcase */
import { useQuery } from '@tanstack/react-query';
import { fetcher } from 'src/api/fetcher';
import { DOWNTIME_STATUS, FULFILLMENT_STATUS } from 'src/constants/maintenance';
import { fetchDataByUris } from 'src/utils/api';
import { API_RESOURCES, PAGINATION_IGNORE_DEFAULT_LIMIT } from 'src/utils/constants';
import { buildLastPerformedByRoot, groupReportsByFulfillment } from 'src/utils/maintenance';
import { getUuid } from 'src/utils/url';

/**
 * The requirement LIST returns `work_checklist` as a URI only — the ordered
 * instructions (and the step count) live on the checklist. Fetch each
 * requirement's checklist and attach it so downstream code can read
 * `requirement.work_checklist.work_instructions`.
 */
export const fetchWorkChecklist = checklistUri =>
  fetcher(`${API_RESOURCES.WORK_CHECKLIST}/${getUuid(checklistUri)}`);

const listParams = params => ({
  ...params,
  'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
});

const enrichWithChecklists = async (requirements, checklistRequests = new Map()) =>
  Promise.all(
    (requirements || []).map(async requirement => {
      if (!requirement.work_checklist || typeof requirement.work_checklist !== 'string') {
        return requirement;
      }
      try {
        if (!checklistRequests.has(requirement.work_checklist)) {
          checklistRequests.set(
            requirement.work_checklist,
            fetchWorkChecklist(requirement.work_checklist)
          );
        }
        const checklist = await checklistRequests.get(requirement.work_checklist);
        return { ...requirement, work_checklist: checklist };
      } catch (_) {
        return { ...requirement, work_checklist: null };
      }
    })
  );

/**
 * React-query reads for Machine Maintenance. Lists come back as
 * `{ resources, meta }`; single GETs return the object. LIST filters use the
 * JSON:API `filter[...]` convention.
 *
 * Eventual-consistency note: do NOT invalidate/refetch a list immediately after
 * mutating it. Seed the cache from the write intent/response instead; immediate
 * follow-up reads can still return the pre-write resource.
 */

export const maintenanceKeys = {
  requirements: (instanceUri, typeUri, includeArchived) => [
    'maintenance-requirements',
    instanceUri,
    typeUri,
    Boolean(includeArchived),
  ],
  events: instanceUri => ['maintenance-events', instanceUri],
  event: uuid => ['maintenance-event', uuid],
  fulfillments: eventUri => ['maintenance-fulfillments', eventUri],
  downtime: uri => ['downtime', uri],
  linkableDowntimes: (field, instanceUri) => ['linkable-downtimes', field, instanceUri],
  revisions: rootUri => ['maintenance-revisions', rootUri],
  fulfillmentsForEvents: eventUris => ['maintenance-fulfillments-for-events', eventUris],
  reportsForFulfillments: fulfillmentUris => [
    'maintenance-reports-for-fulfillments',
    fulfillmentUris,
  ],
  requirementsByUris: uris => ['maintenance-requirements-by-uris', uris],
  resolvedRequirementsByUris: uris => ['maintenance-resolved-requirements-by-uris', uris],
};

/** Additive fetch: instance-scoped requirements + its type-scoped requirements. */
export const useMaintenanceRequirements = (
  instanceTargetUri,
  typeTargetUri,
  includeArchived = false
) =>
  useQuery({
    queryKey: maintenanceKeys.requirements(instanceTargetUri, typeTargetUri, includeArchived),
    enabled: !!instanceTargetUri,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const archivedParam = includeArchived ? { 'filter[include_archived]': true } : {};
      const requests = [
        fetcher(API_RESOURCES.MAINTENANCE_REQUIREMENT, {
          params: listParams({ 'filter[target_uri]': instanceTargetUri, ...archivedParam }),
        }),
      ];
      if (typeTargetUri) {
        requests.push(
          fetcher(API_RESOURCES.MAINTENANCE_REQUIREMENT, {
            params: listParams({ 'filter[target_uri]': typeTargetUri, ...archivedParam }),
          })
        );
      }
      const [instanceResult, typeResult] = await Promise.all(requests);
      const checklistRequests = new Map();
      const [instance, type] = await Promise.all([
        enrichWithChecklists(instanceResult?.resources || [], checklistRequests),
        enrichWithChecklists(typeResult?.resources || [], checklistRequests),
      ]);
      return { instance, type, all: [...instance, ...type] };
    },
  });

/** A single requirement + its checklist by uuid (e.g. a pinned/superseded revision). */
export const useRequirementWithChecklist = (requirementUri, enabled = true) =>
  useQuery({
    queryKey: ['maintenance-requirement', requirementUri],
    enabled: Boolean(enabled && requirementUri),
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const requirement = await fetcher(
        `${API_RESOURCES.MAINTENANCE_REQUIREMENT}/${getUuid(requirementUri)}`
      );
      if (requirement.work_checklist && typeof requirement.work_checklist === 'string') {
        try {
          requirement.work_checklist = await fetchWorkChecklist(requirement.work_checklist);
        } catch (_) {
          requirement.work_checklist = null;
        }
      }
      return requirement;
    },
  });

/**
 * Batch-resolve requirement revisions by uri (current or pinned/superseded),
 * each enriched with its checklist — one chunked LIST + a deduped checklist
 * pass instead of a per-row requirement+checklist fetch. Map keyed by uri.
 */
export const useResolvedRequirementsByUris = (requirementUris = []) => {
  const sorted = [...new Set((requirementUris || []).filter(Boolean))].sort();
  return useQuery({
    queryKey: maintenanceKeys.resolvedRequirementsByUris(sorted),
    enabled: sorted.length > 0,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const byUri = await fetchDataByUris(API_RESOURCES.MAINTENANCE_REQUIREMENT, sorted, {
        'filter[include_superseded]': true,
        'filter[include_archived]': true,
      });
      const enriched = await enrichWithChecklists(Object.values(byUri || {}));
      return enriched.reduce((acc, requirement) => {
        acc[requirement.uri] = requirement;
        return acc;
      }, {});
    },
    select: data => data || {},
  });
};

/** All events (any status) targeting this instance. */
export const useMaintenanceEvents = instanceTargetUri =>
  useQuery({
    queryKey: maintenanceKeys.events(instanceTargetUri),
    enabled: !!instanceTargetUri,
    refetchOnWindowFocus: false,
    // Keep cache-seeded values (post complete/cancel) from being clobbered by an
    // eventually-consistent LIST refetch on the next mount.
    staleTime: 15000,
    queryFn: () =>
      fetcher(API_RESOURCES.MAINTENANCE_EVENT, {
        params: listParams({ 'filter[target_uri]': instanceTargetUri }),
      }),
    select: data => data.resources || [],
  });

/** Fulfillments (selected requirements) of an event. */
export const useEventFulfillments = (eventUri, options = {}) =>
  useQuery({
    queryKey: maintenanceKeys.fulfillments(eventUri),
    enabled: !!eventUri,
    refetchOnWindowFocus: false,
    // See useMaintenanceEvents: protect cache-seeded fulfillment statuses from a
    // lagged LIST refetch right after completion.
    staleTime: 15000,
    queryFn: () =>
      fetcher(API_RESOURCES.MAINTENANCE_EVENT_REQUIREMENT, {
        params: listParams({ 'filter[maintenance_event]': eventUri }),
      }),
    select: data => data.resources || [],
    ...options,
  });

export const useEventFulfillmentsByEventUri = (eventUris = []) =>
  useQuery({
    queryKey: maintenanceKeys.fulfillmentsForEvents(eventUris),
    enabled: eventUris.length > 0,
    refetchOnWindowFocus: false,
    staleTime: 15000,
    queryFn: () =>
      fetcher(API_RESOURCES.MAINTENANCE_EVENT_REQUIREMENT, {
        params: listParams({ 'filter[maintenance_event]': eventUris.join(',') }),
      }),
    select: data =>
      (data.resources || []).reduce((acc, fulfillment) => {
        const eventUri = fulfillment.maintenance_event;
        if (!acc[eventUri]) acc[eventUri] = [];
        acc[eventUri].push(fulfillment);
        return acc;
      }, {}),
  });

export const useFulfillmentReportsByFulfillmentUri = (fulfillmentUris = []) => {
  const sortedFulfillmentUris = [...new Set((fulfillmentUris || []).filter(Boolean))].sort();
  return useQuery({
    queryKey: maintenanceKeys.reportsForFulfillments(sortedFulfillmentUris),
    enabled: sortedFulfillmentUris.length > 0,
    refetchOnWindowFocus: false,
    queryFn: () =>
      fetcher(API_RESOURCES.MAINTENANCE_INSTRUCTION_REPORT, {
        params: listParams({
          'filter[maintenance_event_requirement_fulfillment]': sortedFulfillmentUris.join(','),
        }),
      }),
    select: data => groupReportsByFulfillment(data.resources || []),
  });
};

/** Bulk-load downtimes by uri (one LIST) — map keyed by uri. */
export const useDowntimesByUris = downtimeUris =>
  useQuery({
    queryKey: ['downtimes-by-uris', [...(downtimeUris || [])].sort()],
    enabled: Boolean(downtimeUris && downtimeUris.length),
    refetchOnWindowFocus: false,
    queryFn: () => fetchDataByUris(API_RESOURCES.DOWNTIME, downtimeUris),
    select: data => data || {},
  });

export const useDowntime = (downtimeUri, options = {}) =>
  useQuery({
    queryKey: maintenanceKeys.downtime(downtimeUri),
    enabled: !!downtimeUri,
    refetchOnWindowFocus: false,
    queryFn: () => fetcher(`${API_RESOURCES.DOWNTIME}/${getUuid(downtimeUri)}`),
    ...options,
  });

/** Unlinked downtimes on this workstation available to attach to an event. */
export const useLinkableDowntimes = (workstationField, instanceTargetUri, enabled = true) =>
  useQuery({
    queryKey: maintenanceKeys.linkableDowntimes(workstationField, instanceTargetUri),
    enabled: Boolean(enabled && workstationField && instanceTargetUri),
    refetchOnWindowFocus: false,
    queryFn: () =>
      fetcher(API_RESOURCES.DOWNTIME, {
        params: listParams({
          [`filter[${workstationField}]`]: instanceTargetUri,
          'filter[linkable_to_maintenance]': true,
        }),
      }),
    select: data =>
      (data.resources || []).filter(downtime => downtime.status !== DOWNTIME_STATUS.COMPLETE),
  });

/**
 * "Last performed" per requirement ROOT, from this machine's real history:
 * every fulfillment of every event on the instance, restricted to completed
 * ones, grouped by the pinned revision's root (rapidfab parity). Returns
 * `{ [rootUuid]: completed_at }` — empty while loading / no history.
 */
export const useLastPerformedByRoot = (instanceTargetUri, enabled = true) => {
  const { data: events = [] } = useMaintenanceEvents(instanceTargetUri);
  const eventUris = events
    .map(event => event.uri)
    .filter(Boolean)
    .sort();

  const { data: fulfillments = [] } = useQuery({
    queryKey: maintenanceKeys.fulfillmentsForEvents(eventUris),
    enabled: enabled && eventUris.length > 0,
    refetchOnWindowFocus: false,
    staleTime: 15000,
    queryFn: () =>
      fetcher(API_RESOURCES.MAINTENANCE_EVENT_REQUIREMENT, {
        params: listParams({ 'filter[maintenance_event]': eventUris.join(',') }),
      }),
    select: data => data.resources || [],
  });

  const completedPinnedUris = [
    ...new Set(
      fulfillments
        .filter(
          fulfillment =>
            fulfillment.status === FULFILLMENT_STATUS.COMPLETE &&
            fulfillment.completed_at &&
            fulfillment.maintenance_requirement
        )
        .map(fulfillment => fulfillment.maintenance_requirement)
    ),
  ].sort();

  // Pinned revisions may be superseded/archived — include them or the LIST drops them.
  const { data: pinnedByUri = {} } = useQuery({
    queryKey: maintenanceKeys.requirementsByUris(completedPinnedUris),
    enabled: enabled && completedPinnedUris.length > 0,
    refetchOnWindowFocus: false,
    queryFn: () =>
      fetchDataByUris(API_RESOURCES.MAINTENANCE_REQUIREMENT, completedPinnedUris, {
        'filter[include_superseded]': true,
        'filter[include_archived]': true,
      }),
    select: data => data || {},
  });

  return buildLastPerformedByRoot(fulfillments, pinnedByUri);
};

/** Full revision chain of a requirement (version history). */
export const useRequirementRevisions = (rootUri, enabled = true) =>
  useQuery({
    queryKey: maintenanceKeys.revisions(rootUri),
    enabled: Boolean(enabled && rootUri),
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const data = await fetcher(API_RESOURCES.MAINTENANCE_REQUIREMENT, {
        params: listParams({
          'filter[root_requirement]': rootUri,
          'filter[include_superseded]': true,
          'filter[include_archived]': true,
        }),
      });
      // Each revision pins its own checklist — fetch them so the sheet can show
      // the checklist name + step count (rapidfab parity).
      return enrichWithChecklists(data.resources || []);
    },
    select: data =>
      [...(data || [])].sort((a, b) => (b.revision_number ?? 0) - (a.revision_number ?? 0)),
  });
